import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert,
  MenuItem, IconButton, Table, TableHead, TableBody, TableRow,
  TableCell, Divider, Chip, Tooltip
} from '@mui/material';
import { Add, Delete, ArrowBack, HomeWork, Receipt } from '@mui/icons-material';
import axios from 'axios';
import { useSuppliers } from '../hooks/useSuppliers';
import { fmt, sym } from '../utils/currency';
import { useItems } from '../hooks/useItems';
import { isRealDate } from '../utils/dateValidation';
import { useGLAccounts } from '../hooks/useGLAccounts';
import { glAccountOptions } from '../utils/glAccountOptions';
import { useLocations } from '../hooks/useLocations';
import { useVATGroups } from '../hooks/useVATGroups';

const API = process.env.REACT_APP_API_URL;
const LINE_TYPES = ['Item', 'Service', 'GL Account', 'Fixed Asset'];

const emptyLine = () => ({
  _key: Date.now() + Math.random(),
  lineType: 'Service', itemNo: '', faNo: '', glAccount: '',
  description: '', qty: 1, unitPrice: '', amount: 0, vatGroupCode: ''
});

// Raise a Purchase Invoice directly, with no preceding PO — for expense/service
// bills and ad-hoc purchases. Posts GL (Dr lines + Input VAT, Cr AP) immediately;
// Item lines are also received into stock.
const PurchaseInvoiceForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { suppliers }            = useSuppliers();
  const { items: allItems }      = useItems();
  const { accounts: glAccounts } = useGLAccounts();
  const { locations }            = useLocations();
  const { vatGroups }            = useVATGroups();

  const [fixedAssets, setFixedAssets]     = useState([]);
  const [postingGroups, setPostingGroups] = useState([]);

  const [supplierCode,     setSupplierCode]     = useState('');
  const [invoiceDate,      setInvoiceDate]      = useState(new Date().toISOString().split('T')[0]);
  const [dueDate,          setDueDate]          = useState('');
  const [supplierInvoiceNo,setSupplierInvoiceNo]= useState('');
  const [locationCode,     setLocationCode]     = useState('');
  const [lines,            setLines]            = useState([emptyLine()]);
  const [error,            setError]            = useState('');
  const [saving,           setSaving]           = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/fixed-assets`,                { headers }),
      axios.get(`${API}/api/fixed-assets/posting-groups`, { headers }),
    ]).then(([faRes, pgRes]) => {
      setFixedAssets(faRes.data);
      setPostingGroups(pgRes.data);
    }).catch(() => {}); // non-fatal
  }, []); // eslint-disable-line

  // supplier-preselect: open prefilled from the vendor card
  useEffect(() => { const sc = searchParams.get('supplier'); if (sc) setSupplierCode(sc); }, [searchParams]);

  const itemsOfType = (type) => allItems.filter(i => i.itemType === type);
  const hasItemLines = lines.some(l => l.lineType === 'Item');

  const getAcqAccount = (faNo) => {
    const fa = fixedAssets.find(a => a.faNo === faNo);
    if (!fa) return '';
    const pg = postingGroups.find(p => p.groupCode === fa.faPostingGroup);
    return pg?.acquisitionCostAccount || '';
  };

  const updateLine = (key, field, value) => {
    setLines(prev => prev.map(l => {
      if (l._key !== key) return l;
      const updated = { ...l, [field]: value };
      if (field === 'lineType') {
        updated.itemNo = ''; updated.faNo = ''; updated.glAccount = '';
        updated.description = ''; updated.unitPrice = '';
        if (value === 'Fixed Asset') updated.qty = 1;
      }
      if (field === 'itemNo' && value) {
        const item = allItems.find(i => i.itemNo === value);
        if (item) { updated.glAccount = item.glAccount; updated.description = item.itemName; updated.unitPrice = item.unitPrice; }
      }
      if (field === 'faNo' && value) {
        const fa = fixedAssets.find(a => a.faNo === value);
        if (fa) { updated.glAccount = getAcqAccount(value); updated.description = fa.faName; updated.qty = 1; }
      }
      const qty   = parseFloat(field === 'qty'       ? value : updated.qty)       || 0;
      const price = parseFloat(field === 'unitPrice' ? value : updated.unitPrice) || 0;
      updated.amount = parseFloat((qty * price).toFixed(2));
      return updated;
    }));
  };

  const addLine    = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (key) => setLines(prev => prev.filter(l => l._key !== key));

  const vatPct  = (code) => vatGroups.find(g => g.code === code)?.vatPercent || 0;
  const lineVAT = (l) => parseFloat(((l.amount || 0) * vatPct(l.vatGroupCode) / 100).toFixed(2));
  const subtotal    = lines.reduce((s, l) => s + (l.amount || 0), 0);
  const totalVAT    = lines.reduce((s, l) => s + lineVAT(l), 0);
  const totalAmount = subtotal + totalVAT;

  const handleSubmit = async () => {
    if (!supplierCode) { setError('Supplier is required'); return; }
    if (hasItemLines && !locationCode) { setError('Receive-to Location is required when the invoice has Item lines'); return; }
    for (const l of lines) {
      if (l.lineType === 'Fixed Asset') {
        if (!l.faNo)      { setError('Select a Fixed Asset on each FA line'); return; }
        if (!l.glAccount) { setError(`FA ${l.faNo}: posting group has no Acquisition Cost Account configured`); return; }
      } else if (!l.glAccount) { setError('Each line must have a GL Account'); return; }
      if (!l.description.trim()) { setError('Each line must have a description'); return; }
      if (!l.qty || l.qty <= 0)  { setError('Qty must be greater than zero'); return; }
      if (!l.unitPrice || parseFloat(l.unitPrice) <= 0) { setError('Unit price must be greater than zero'); return; }
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        supplierCode, invoiceDate, dueDate, supplierInvoiceNo, locationCode,
        lines: lines.map((l, i) => ({
          lineNo: i + 1, lineType: l.lineType, itemNo: l.itemNo || '', faNo: l.faNo || '',
          glAccount: l.glAccount, description: l.description,
          qty: parseFloat(l.qty), unitPrice: parseFloat(l.unitPrice), amount: l.amount,
          vatGroupCode: l.vatGroupCode || ''
        }))
      };
      await axios.post(`${API}/api/payables/invoices`, payload, { headers });
      navigate('/payables/invoices');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to post invoice');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" mb={2} gap={1} flexWrap="wrap">
            <IconButton onClick={() => navigate('/payables/invoices')} sx={{ mr: 1 }}>
              <ArrowBack />
            </IconButton>
            <Receipt sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h5" color="primary">New Purchase Invoice (direct)</Typography>
            <Box ml="auto" display="flex" gap={1}>
              <Button variant="contained" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Posting…' : 'Post Invoice'}
              </Button>
              <Button variant="outlined" onClick={() => navigate('/payables/invoices')}>Cancel</Button>
            </Box>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Posts straight to the ledger (Dr expense/asset + Input VAT, Cr Accounts Payable) with no
            purchase order. Item lines are also received into stock. Use this for utilities, rent,
            services and ad-hoc buys; for stocked procurement start from a Quote or Order instead.
          </Alert>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box display="flex" gap={2} flexWrap="wrap" mb={3}>
            <TextField select label="Supplier" value={supplierCode}
              onChange={e => setSupplierCode(e.target.value)} sx={{ minWidth: 280 }} size="small">
              <MenuItem value=""><em>Select Supplier</em></MenuItem>
              {suppliers.map(s => (
                <MenuItem key={s.supplierCode} value={s.supplierCode}>{s.supplierCode} – {s.supplierName}</MenuItem>
              ))}
            </TextField>
            <TextField label="Invoice Date" type="date" value={invoiceDate}
              onChange={e => setInvoiceDate(e.target.value)} size="small" InputLabelProps={{ shrink: true }}
              error={!!(invoiceDate && !isRealDate(invoiceDate))}
              helperText={invoiceDate && !isRealDate(invoiceDate) ? 'Not a valid date' : ''} />
            <TextField label="Due Date" type="date" value={dueDate}
              onChange={e => setDueDate(e.target.value)} size="small" InputLabelProps={{ shrink: true }}
              inputProps={{ min: invoiceDate || undefined }}
              error={!!((dueDate && !isRealDate(dueDate)) || (invoiceDate && dueDate && dueDate < invoiceDate))}
              helperText={(dueDate && !isRealDate(dueDate)) ? 'Not a valid date' : (invoiceDate && dueDate && dueDate < invoiceDate) ? 'Before invoice date' : ''} />
            <TextField label="Supplier Invoice No" value={supplierInvoiceNo}
              onChange={e => setSupplierInvoiceNo(e.target.value)} size="small" sx={{ minWidth: 180 }}
              helperText="Vendor's own invoice ref" />
            <TextField select label="Receive-to Location" value={locationCode}
              onChange={e => setLocationCode(e.target.value)} sx={{ minWidth: 220 }} size="small"
              helperText={hasItemLines ? 'Required — items received here' : 'Optional for service/GL/FA'}>
              <MenuItem value=""><em>Select Location</em></MenuItem>
              {locations.map(loc => (
                <MenuItem key={loc.locationCode} value={loc.locationCode}>{loc.locationCode} – {loc.locationName}</MenuItem>
              ))}
            </TextField>
          </Box>

          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" fontWeight={600} mb={1}>Invoice Lines</Typography>

          <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 120 }}><strong>Type</strong></TableCell>
                <TableCell sx={{ minWidth: 190 }}><strong>Item / FA / GL</strong></TableCell>
                <TableCell sx={{ minWidth: 170 }}><strong>Description</strong></TableCell>
                <TableCell align="center" sx={{ width: 70 }}><strong>Qty</strong></TableCell>
                <TableCell align="right" sx={{ minWidth: 110 }}><strong>Unit Price</strong></TableCell>
                <TableCell sx={{ minWidth: 120 }}><strong>VAT</strong></TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}><strong>Amount</strong></TableCell>
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map(l => (
                <TableRow key={l._key}>
                  <TableCell>
                    <TextField select value={l.lineType} size="small" fullWidth
                      onChange={e => updateLine(l._key, 'lineType', e.target.value)}>
                      {LINE_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    {l.lineType === 'Fixed Asset' ? (
                      <TextField select value={l.faNo} size="small" fullWidth
                        onChange={e => updateLine(l._key, 'faNo', e.target.value)}>
                        <MenuItem value=""><em>Select Fixed Asset</em></MenuItem>
                        {fixedAssets.map(fa => (
                          <MenuItem key={fa.faNo} value={fa.faNo}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <HomeWork fontSize="small" sx={{ color: 'text.secondary' }} />
                              {fa.faNo} — {fa.faName}
                            </Box>
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : l.lineType === 'GL Account' ? (
                      <TextField select value={l.glAccount} size="small" fullWidth
                        onChange={e => updateLine(l._key, 'glAccount', e.target.value)}>
                        {glAccountOptions(glAccounts)}
                      </TextField>
                    ) : (
                      <TextField select value={l.itemNo} size="small" fullWidth
                        onChange={e => updateLine(l._key, 'itemNo', e.target.value)}>
                        <MenuItem value=""><em>Select {l.lineType}</em></MenuItem>
                        {itemsOfType(l.lineType).map(i => (
                          <MenuItem key={i.itemNo} value={i.itemNo}>{i.itemNo} – {i.itemName}</MenuItem>
                        ))}
                      </TextField>
                    )}
                    {l.lineType === 'Fixed Asset' && l.glAccount && (
                      <Tooltip title="Acquisition Cost Account from FA Posting Group">
                        <Chip label={l.glAccount} size="small" color="primary" variant="outlined"
                          sx={{ mt: 0.5, fontSize: '0.65rem' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <TextField value={l.description} size="small" fullWidth
                      onChange={e => updateLine(l._key, 'description', e.target.value)} />
                  </TableCell>
                  <TableCell align="center">
                    <TextField value={l.qty} size="small" type="number"
                      inputProps={{ min: 0, step: 1, style: { textAlign: 'center' } }}
                      onChange={e => updateLine(l._key, 'qty', e.target.value)}
                      disabled={l.lineType === 'Fixed Asset'} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField value={l.unitPrice} size="small" type="number"
                      inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                      onChange={e => updateLine(l._key, 'unitPrice', e.target.value)}
                      label={l.lineType === 'Fixed Asset' ? 'Acq. Cost' : undefined} />
                  </TableCell>
                  <TableCell>
                    <TextField select value={l.vatGroupCode || ''} size="small" fullWidth
                      onChange={e => updateLine(l._key, 'vatGroupCode', e.target.value)}>
                      <MenuItem value=""><em>No VAT</em></MenuItem>
                      {vatGroups.map(g => (
                        <MenuItem key={g.code} value={g.code}>{g.code} ({g.vatPercent}%)</MenuItem>
                      ))}
                    </TextField>
                    {l.vatGroupCode && lineVAT(l) > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        VAT {sym()} {fmt(lineVAT(l))}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{sym()} {fmt(l.amount)}</TableCell>
                  <TableCell>
                    <IconButton size="small" color="error" onClick={() => removeLine(l._key)}
                      disabled={lines.length === 1}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mt={2}>
            <Button startIcon={<Add />} onClick={addLine} variant="outlined" size="small">Add Line</Button>
            <Box ml="auto" textAlign="right">
              <Typography variant="body2" color="text.secondary">Subtotal: {sym()} {fmt(subtotal)}</Typography>
              <Typography variant="body2" color="text.secondary">VAT: {sym()} {fmt(totalVAT)}</Typography>
              <Typography variant="h6" fontWeight={700}>Total: {sym()} {fmt(totalAmount)}</Typography>
            </Box>
          </Box>

        </CardContent>
      </Card>
    </Box>
  );
};

export default PurchaseInvoiceForm;
